"use client"

import type React from "react"
import { AppShell } from "@/components/layout/app-shell"
import { useAuth } from "@/src/features/auth/hooks"
import { useRouter } from "next/navigation"
import { useEffect } from "react"

// Demo mode - bypasses authentication for demonstration purposes
// Enabled by default - set NEXT_PUBLIC_DEMO_MODE=false to disable
const DEMO_MODE = process.env.NEXT_PUBLIC_DEMO_MODE !== "false"

export default function AppLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const { user, loading } = useAuth()
  const router = useRouter()

  useEffect(() => {
    // Skip auth check in demo mode
    if (DEMO_MODE) return
    
    if (!loading && !user) {
      router.push("/login")
    }
  }, [user, loading, router])

  // In demo mode, render immediately without auth checks
  if (DEMO_MODE) {
    return <AppShell>{children}</AppShell>
  }

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Don't render protected content until user is confirmed
  if (!user) {
    return null
  }

  return <AppShell>{children}</AppShell>
}
